<i18n>
{
	"en": {
		"tripsFound": "{count} found",
		"trip": "Trip",
		"placeholder": "Search for Trip",
		"selectedTrip": "Selected Trip: {number}"
	},
	"no": {
		"tripsFound": "{count} treff",
		"trip": "Tur",
		"placeholder": "Søk etter tur",
		"selectedTrip": "Valgt tur: {number}"
	}
}
</i18n>

<template>
	<t-input-card
		:header="$t('trip')"
		:title="$t('selectedTrip', {number: selectedTrip ? selectedTrip.tripNumber || selectedTrip.tripNumber : ''})"
		:hideContent="!selectedTrip || !selectedTrip.tripNumber"
		enableCardClick
		hidePadding
		@click="onCardClicked"
	>
		<template #input>
			<t-combobox
				ref="input"

				:searchValue="searchValue"
				:items="trips"
				:selected="selectedTrip"

				:disabled="disabled"
				disableButtonTabbing

				:filterFunction="filterFunction"
				:sortFunction="sortFunction"

				@input="onInput"
				@blur="onBlur"
				@change="onChange"
			>
				<template #optionItemContent="{ item }">
					<div class="item">
						<div class="item-main-text">{{ item.tripNumber }}</div>
						<div class="item-sub-text">
							<div>{{ item.fromAddress.name }}, {{ item.fromAddress.street }}, {{ item.fromAddress.postalCode }} {{ item.fromAddress.postalPlace }}</div>
							<div>{{ item.toAddress.name }}, {{ item.toAddress.street }}, {{ item.toAddress.postalCode }} {{ item.toAddress.postalPlace }}</div>
						</div>
					</div>
				</template>
			</t-combobox>
		</template>

		<template #contentText>
			<div class="trip-details">
				<div style="grid-area: from;">
					{{selectedTrip.toAddress | formatAddress}}
				</div>
				<div style="grid-area: to;">
					{{selectedTrip.toAddress | formatAddress}}
				</div>

				<div style="grid-area: etd;">
					{{selectedTrip.etd | formatDate}}
				</div>
				<div style="grid-area: eta;">
					{{selectedTrip.eta | formatDate}}
				</div>

				<div style="grid-area: vendor;">
					{{selectedTrip.vendor | formatVendor}}
				</div>
				<div style="grid-area: cargoCarrier;">
					{{selectedTrip.cargoCarrier | formatCargoCarrier}}
				</div>
			</div>
		</template>

		<template #title-after>
			<slot name="input-card-after" />
		</template>
	</t-input-card>
</template>

<script>
import { mapActions } from './store/trips.store.js';
import formMixin from '@/components/evergreen/inputs/formMixin';
import { displayDateFormatter, eventBus } from '@/core';

const formatDateTime = date => date ? displayDateFormatter(date, true) : 'N/A';
const formatDate = date => date ? displayDateFormatter(date, false) : 'N/A';

export default {
	SERVER_RETURN_LIMIT: 50,
	name: 'TripsSearchInput',

	filters: {
		formatAddress(addressObj) {
			return addressObj && addressObj.name;
		},
		formatDate(dateObj) {
			if (dateObj) return dateObj.hasTime ? formatDateTime(dateObj.date) : formatDate(dateObj.date);
			return '';
		},
		formatVendor(vendor) {
			return vendor && vendor.name;
		},
		formatCargoCarrier(cargoCarrier) {
			return cargoCarrier && cargoCarrier.registrationNumber;
		},
	},

	mixins: [formMixin],
	props: {
		title: String,
		selectedTrip: [Object, String],
		defaultValue: {
			type: [Object, String],
			default() {
				return undefined;
			},
		},
	},

	data() {
		return {
			trips: [],
			searchValue: '',
			lastSearch: null,
			searchTimeout: null
		};
	},

	computed: {
		shouldShowPopover() {
			return this.popoverIsVisible && this.filteredItems.length > 0;
		},
	},

	watch: {
		searchValue: {
			immediate: true,
			handler(newVal) {
				if (newVal) {
					clearTimeout(this.searchTimeout);
					this.searchTimeout = setTimeout(() => {
						this.refetchTrips();
					},200);
				}
			}
		}
	},

	methods: {
		...mapActions(['getTrips']),
		async refetchTrips() {
			//don't query server if server doesn't have anymore for you
			if (this.trips.length < this.$options.SERVER_RETURN_LIMIT && this.searchValue.startsWith(this.lastSearch)) return;
			this.trips = await this.getTrips(this.searchValue);
			this.lastSearch = this.searchValue;
		},

		filterItem(item, searchString) {
			if (!item) return false;
			let ret = true;
			for (let c of searchString.split(' ')) {
				ret = ret &&
					( (item.tripNumber && item.tripNumber.toString().toLowerCase().includes(c))
					|| ( item.fromAddress && item.fromAddress.name && item.fromAddress.name.toString().toLowerCase().includes(c) )
					|| ( item.toAddress && item.toAddress.name && item.toAddress.name.toString().toLowerCase().includes(c) )
					);
			}
			return ret;
		},

		clearInput() {
			this.onInput('');
		},
		onInput(value) {
			this.searchValue = value;
		},

		onCardClicked() {
			eventBus.$emit('setEditTrip', this.selectedTrip.id);
			eventBus.$emit('toggleNewTripModal',true);
		},

		onChange(trip) {
			if (trip) {
				this.searchValue = this.getTripNumber(trip);
				this.$emit('change', trip);
			} else
				this.$emit('change', this.defaultValue);
		},

		onBlur() {
			if (this.searchValue === '') {
				this.$emit('change', this.defaultValue);
				return;
			}
			let matchingTrip = this.trips.find(matchingTrip => this.searchValue === matchingTrip.tripNumber);
			if (matchingTrip && matchingTrip !== this.selectedTrip) {
				this.$emit('change', matchingTrip);
			} else {
				this.searchValue = this.getTripNumber(this.selectedTrip);
			}
		},

		getTripNumber(trip) {
			if (!trip || !trip.tripNumber) return '';
			return trip.tripNumber;
		},

		filterFunction(trip) {
			if (!trip) return false;
			let result = true;
			for (let c of this.searchValue.split(' ')) {
				result = result
					&& ( (trip.tripNumber && trip.tripNumber.toString().toLowerCase().includes(c))
					|| ( trip.fromAddress && trip.fromAddress.name && trip.fromAddress.name.toString().toLowerCase().includes(c) )
					|| ( trip.toAddress && trip.toAddress.name && trip.toAddress.name.toString().toLowerCase().includes(c) )
					);
			}
			return result;
		},
		sortFunction(a, b) {
			if (this.searchValue === a.tripNumber) return -1;
			if (this.searchValue === b.tripNumber) return 1;
			if (Number(a.tripNumber) > Number(b.tripNumber)) return -1;
			if (Number(a.tripNumber) < Number(b.tripNumber)) return 1;
			return 0;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
.item {
	display: flex;
	flex-direction: row;
	height: 100%;
}

.item-main-text {
	height:32px;
	line-height: 32px;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
	padding-right: 10px;
}

.item-sub-text {
	height:32px;
	font-size: 10px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: 1.6;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}

.trip-details {
	display: grid;
	row-gap: 5px;
	column-gap: 5px;
	grid-template-columns: repeat(2, 1fr);
	grid-template-areas:
		"input input"
		"from  to"
		"etd   eta"
		"vendor cargoCarrier";
}
</style>
